import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CircleCheck, Inbox, Plus, PowerOff, RefreshCw, Search, SearchX, Settings, Telescope } from 'lucide-react';
import {
  Button,
  Callout,
  Checkbox,
  DataTable,
  EmptyState,
  Input,
  NativeSelect,
  Status,
  Tabs,
  formatDatumZeit,
  formatEuro,
  toast,
  type Column,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { radarApi } from '@/lib/api';
import type { RadarKind, RadarProgram, RadarSort, RadarStatus } from '@/types';
import { EFFORT_TEXT, KIND_PLURAL, KIND_TEXT, REWARD_KIND_TEXT, SORT_TEXT, STATUS_REIHENFOLGE, optionen, statusText } from './radarText';
import { EventDatum, FristZelle, PruefHinweis, Quellzustand, Score, betragText, quelleBrauchtJemanden, useVorwarnungTage } from './Bausteine';
import { PRUEFAUFTRAEGE_ANKER, PruefauftraegeBlock } from './PruefauftraegeBlock';
import { REITER, adresseAusFilter, filterAusAdresse, istReiter, type ListenFilter } from './listenAdresse';
import { ProgramDialog } from './ProgramDialog';
import { AkteureTab } from './AkteureTab';
import { LaeufeBlock } from './LaeufeBlock';
import { StillstandHinweise } from './StillstandHinweise';

/**
 * Das Radar — die Arbeitsliste für Förderung, Wettbewerbe und Veranstaltungen.
 *
 * Oben die Prüfaufträge (was der Mensch tun muss, Anker `#pruefauftraege`),
 * darunter die Reiter. Alle Filter stehen in der URL (`listenAdresse.ts`).
 * Jeder Eintrag hat seine eigene Seite `/radar/:id` (Auftrag 010, D2).
 */
export function RadarPage() {
  const [suchparameter] = useSearchParams();

  // Alte Adressen aus Mails und der Glocke (bis D2): `?program=<id>` öffnete
  // den Dialog, `?tab=pruefauftraege` meinte den Block ganz oben.
  const altesProgramm = suchparameter.get('program');
  if (altesProgramm) return <Navigate to={`/radar/${encodeURIComponent(altesProgramm)}`} replace />;
  if (suchparameter.get('tab') === 'pruefauftraege') return <Navigate to={`/radar#${PRUEFAUFTRAEGE_ANKER}`} replace />;

  return <RadarListe />;
}

function RadarListe() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [suchparameter, setSuchparameter] = useSearchParams();

  const { reiter, status, suche, sortierung, erledigte: erledigteAnzeigen } = filterAusAdresse(suchparameter);

  // `replace`: Filtern und Tippen in der Suche legen keine neuen Einträge im
  // Verlauf des Browsers an; „Zurück“ führt aus der Liste heraus, nicht durch
  // jeden Buchstaben.
  const adresseSetzen = (neu: Partial<ListenFilter>) => {
    setSuchparameter(adresseAusFilter({ ...filterAusAdresse(suchparameter), ...neu }), { replace: true });
  };
  const reiterWaehlen = (wert: string) => {
    if (istReiter(wert)) adresseSetzen({ reiter: wert });
  };

  const [dialogOffen, setDialogOffen] = useState(false);

  const vorwarnungTage = useVorwarnungTage();
  const oeffnen = (p: RadarProgram) => navigate(`/radar/${p.id}`, { state: { listenSuche: location.search } });

  const zusammenfassung = useQuery({
    queryKey: ['radar', 'summary'],
    queryFn: radarApi.summary,
  });

  const art: RadarKind | null = reiter === 'akteure' || reiter === 'alle' ? null : reiter;
  const filter = useMemo(
    () => ({
      kind: art ?? undefined,
      status,
      suche,
      include_closed: erledigteAnzeigen,
      sort: sortierung,
    }),
    [art, status, suche, erledigteAnzeigen, sortierung]
  );

  const programme = useQuery({
    queryKey: ['radar', 'programs', filter],
    queryFn: () => radarApi.programs(filter),
    enabled: reiter !== 'akteure',
  });

  // Prüfaufträge über alle Arten — unabhängig vom Reiter und den Filtern.
  const pruefauftraegeFaellig = (zusammenfassung.data?.manual_check_due_count ?? 0) > 0;
  const pruefauftraege = useQuery({
    queryKey: ['radar', 'programs', 'pruefauftraege'],
    queryFn: () => radarApi.programs({ sort: 'deadline' }),
    enabled: pruefauftraegeFaellig,
    select: (liste) => liste.filter((p) => p.manual_check_due),
  });

  useEffect(() => {
    if (zusammenfassung.isError) toast.error('Radar-Übersicht konnte nicht geladen werden');
  }, [zusammenfassung.isError]);

  useEffect(() => {
    if (programme.isError) toast.error('Einträge konnten nicht geladen werden');
  }, [programme.isError]);

  // `#pruefauftraege` (Mail „Quellen händisch prüfen“): hinscrollen, sobald
  // der Block steht. Er steht erst nach zwei Abfragen da.
  const zumBlock = location.hash === `#${PRUEFAUFTRAEGE_ANKER}`;
  const blockDa = Boolean(pruefauftraege.data?.length) || zusammenfassung.data?.manual_check_due_count === 0;
  useEffect(() => {
    if (zumBlock && blockDa) document.getElementById(PRUEFAUFTRAEGE_ANKER)?.scrollIntoView({ block: 'start' });
  }, [zumBlock, blockDa]);

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['radar'] });
  };

  // „Jetzt prüfen“ / „Jetzt suchen“ — der Lauf läuft im Worker; hier wird nur eingereiht.
  const laufFehler = (fehler: { response?: { data?: { detail?: unknown } } }) =>
    toast.error(
      typeof fehler.response?.data?.detail === 'string' ? fehler.response.data.detail : 'Der Lauf konnte nicht gestartet werden'
    );
  const pruefen = useMutation({
    mutationFn: () => radarApi.startRun('monitor'),
    onSuccess: () => {
      toast.success('Prüflauf eingereiht — das Ergebnis erscheint unter „Letzte Läufe“.');
      void queryClient.invalidateQueries({ queryKey: ['radar', 'runs'] });
    },
    onError: laufFehler,
  });
  const suchen = useMutation({
    mutationFn: () => radarApi.startRun('discovery'),
    onSuccess: () => {
      toast.success('Suche eingereiht — sie dauert einige Minuten; neue Funde erscheinen als „Neu“.');
      void queryClient.invalidateQueries({ queryKey: ['radar', 'runs'] });
    },
    onError: laufFehler,
  });

  const gefiltert = Boolean(status || suche.trim() || erledigteAnzeigen);
  const listenName = art ? KIND_PLURAL[art] : 'Einträge';
  const liste = programme.data ?? [];
  const zaehler = zusammenfassung.data?.by_kind ?? {};
  const radarAus = zusammenfassung.data?.enabled === false;

  // Weitere Aktionen: am Rechner als Knöpfe vor „Eintrag anlegen“, am Handy im
  // Menü „…“. Was gerade nicht geht, bleibt mit Grund sichtbar (bis D2 fehlte
  // „Jetzt prüfen“ bei ausgeschaltetem Radar einfach).
  const daten = zusammenfassung.data;
  const mehrAktionen = [
    { label: 'Einstellungen', icon: Settings, onSelect: () => navigate('/einstellungen?reiter=radar') },
    {
      label: 'Jetzt suchen',
      icon: Telescope,
      onSelect: () => suchen.mutate(),
      loading: suchen.isPending,
      gesperrt: daten && !daten.discovery_enabled ? 'Die Suche ist ausgeschaltet (Einstellungen).' : undefined,
    },
    {
      label: 'Jetzt prüfen',
      icon: RefreshCw,
      onSelect: () => pruefen.mutate(),
      loading: pruefen.isPending,
      gesperrt: radarAus ? 'Das Radar ist ausgeschaltet (Einstellungen).' : undefined,
    },
  ];

  const filterZuruecksetzen = () => {
    adresseSetzen({ status: '', suche: '', erledigte: false });
  };

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Radar"
        subtitle={beschreibung(zusammenfassung.data?.last_monitor_at)}
        mehrAktionen={mehrAktionen}
        actions={
          reiter !== 'akteure' && (
            <Button variant="primary" icon={Plus} onClick={() => setDialogOffen(true)}>
              Eintrag anlegen
            </Button>
          )
        }
      />

      {radarAus && (
        <Callout tone="neutral" icon={PowerOff} title="Radar ausgeschaltet" boxed>
          Die Liste wird nicht automatisch gepflegt. Einschalten unter Einstellungen.
        </Callout>
      )}

      <StillstandHinweise stillstand={zusammenfassung.data?.stillstand} />

      {pruefauftraegeFaellig ? (
        <PruefauftraegeBlock programme={pruefauftraege.data ?? []} onGeaendert={aktualisieren} listenSuche={location.search} />
      ) : (
        zumBlock &&
        zusammenfassung.data && (
          <section id={PRUEFAUFTRAEGE_ANKER} className="scroll-mt-4">
            <Callout tone="neutral" icon={CircleCheck} title="Keine Prüfaufträge offen" boxed>
              Alle Quellen, die das Radar nicht selbst lesen kann, sind geprüft.
            </Callout>
          </section>
        )
      )}

      <Tabs
        label="Arten"
        items={REITER.map((r) => ({
          value: r.wert,
          label: r.label,
          count: r.wert === 'akteure' ? undefined : r.wert === 'alle' ? zusammenfassung.data?.open_count : (zaehler[r.wert] ?? undefined),
        }))}
        value={reiter}
        onChange={reiterWaehlen}
      />

      {reiter === 'akteure' ? (
        <AkteureTab />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              icon={Search}
              aria-label="Einträge durchsuchen"
              placeholder="Titel, Träger, Inhalt …"
              value={suche}
              onChange={(e) => adresseSetzen({ suche: e.target.value })}
              className="w-full sm:w-64"
            />
            <NativeSelect
              aria-label="Status"
              options={[
                { value: '', label: 'Status: alle offenen' },
                ...STATUS_REIHENFOLGE.map((wert) => ({ value: wert, label: statusText(wert) })),
              ]}
              value={status}
              onChange={(e) => adresseSetzen({ status: e.target.value as RadarStatus | '' })}
              className="w-full sm:w-52"
            />
            <NativeSelect
              aria-label="Sortierung"
              options={optionen(SORT_TEXT).map((o) => ({ ...o, label: `Sortierung: ${o.label}` }))}
              value={sortierung}
              onChange={(e) => adresseSetzen({ sortierung: e.target.value as RadarSort })}
              className="w-full sm:w-52"
            />
            <Checkbox
              label="Erledigte anzeigen"
              checked={erledigteAnzeigen}
              onChange={(wert) => adresseSetzen({ erledigte: wert })}
            />
          </div>

          <DataTable
            columns={spalten(art, vorwarnungTage)}
            rows={liste}
            loading={programme.isLoading}
            onRowClick={oeffnen}
            label={listenName}
            empty={
              gefiltert ? (
                <EmptyState
                  icon={SearchX}
                  title="Nichts gefunden"
                  description="Kein Eintrag passt zu Suche und Filter."
                  action={<Button onClick={filterZuruecksetzen}>Filter zurücksetzen</Button>}
                />
              ) : (
                <EmptyState
                  icon={Inbox}
                  title={`Noch keine ${listenName}`}
                  description="Das Radar füllt die Liste bei der nächsten Suche; Tipps lassen sich auch von Hand eintragen."
                  action={
                    // Die Hauptaktion steht im Kopf; hier dieselbe Aktion zweitrangig
                    // (höchstens eine Hauptaktion je Ansicht).
                    <Button size="lg" icon={Plus} onClick={() => setDialogOffen(true)}>
                      Eintrag anlegen
                    </Button>
                  }
                />
              )
            }
          />
        </>
      )}

      <LaeufeBlock />

      <ProgramDialog
        offen={dialogOffen}
        onSchliessen={() => setDialogOffen(false)}
        onGespeichert={aktualisieren}
        art={art ?? 'foerderung'}
      />

    </div>
  );
}

function beschreibung(zuletzt: string | null | undefined): string {
  const basis = 'Förderprogramme, Wettbewerbe und Veranstaltungen mit Fristen — was ansteht, was läuft, was erledigt ist.';
  return zuletzt ? `${basis} Zuletzt automatisch geprüft ${formatDatumZeit(zuletzt)}.` : basis;
}

const leer = <span className="text-muted-foreground">–</span>;

/**
 * Die Spalten je Reiter — Titel, Score und Status überall, die Mitte je nach
 * Art. Eine Quelle, die jemanden braucht, steht als Wort unter dem Titel (bis
 * D2 eine eigene Spalte mit Symbol und Tooltip). „Alle“ (`art` null) zeigt statt der Mitte die Art und die
 * nächste Frist, bei Veranstaltungen ohne Anmeldeschluss das Datum.
 */
function spalten(art: RadarKind | null, vorwarnungTage: number): Column<RadarProgram>[] {
  const titel: Column<RadarProgram> = {
    key: 'title',
    header: 'Titel',
    strong: true,
    mobil: 'titel',
    // Feste Breite: `truncate` greift nur, wenn die Zelle nicht mitwaechst —
    // sonst schieben lange Titel Score und Status aus dem Bild.
    width: '18rem',
    render: (p) => (
      <div className="min-w-0 max-w-[17rem]">
        <div className="truncate">{p.title}</div>
        {(p.actor_name || p.provider) && (
          <div className="truncate t-small font-normal text-muted-foreground">{p.actor_name ?? p.provider}</div>
        )}
        <Handlungsbedarf programm={p} />
      </div>
    ),
    renderMobil: (p) => p.title,
  };
  const score: Column<RadarProgram> = {
    key: 'score',
    header: 'Score',
    render: (p) => <Score score={p.score} />,
  };
  const status: Column<RadarProgram> = {
    key: 'status',
    header: 'Status',
    mobil: 'status',
    render: (p) => <Status status={`radar_${p.status}`} variant="plain" />,
  };
  const frist = (header: string): Column<RadarProgram> => ({
    key: 'deadline',
    header,
    mobil: 'unter',
    render: (p) => <FristZelle programm={p} vorwarnungTage={vorwarnungTage} />,
    // Am Handy teilt sich die Frist eine Zeile mit dem Status, die abschneidet:
    // „Fällig am 15.11.2026, in 18 Tag…“. Ohne Restlaufzeit passt sie; der Ton
    // sagt weiter, ob es eilt.
    renderMobil: (p) => <FristZelle programm={p} vorwarnungTage={vorwarnungTage} relativ={false} />,
  });

  if (art === null) {
    return [
      titel,
      { key: 'kind', header: 'Art', render: (p) => KIND_TEXT[p.kind] },
      {
        key: 'deadline',
        header: 'Frist',
        mobil: 'unter',
        render: (p) =>
          !p.deadline && p.kind === 'event' && p.event_date ? (
            <EventDatum programm={p} />
          ) : (
            <FristZelle programm={p} vorwarnungTage={vorwarnungTage} />
          ),
        renderMobil: (p) =>
          !p.deadline && p.kind === 'event' && p.event_date ? (
            <EventDatum programm={p} />
          ) : (
            <FristZelle programm={p} vorwarnungTage={vorwarnungTage} relativ={false} />
          ),
      },
      score,
      status,
    ];
  }

  if (art === 'foerderung') {
    return [
      titel,
      { key: 'subkind', header: 'Art', render: (p) => p.subkind ?? leer },
      {
        key: 'amount',
        header: 'Betrag / Quote',
        // Konditionen sind oft lange Saetze — gekuerzt anzeigen, sonst schieben
        // sie Frist, Score und Status aus dem Bild. Der volle Text steht im Tooltip.
        render: (p) => {
          const text = betragText(p);
          return (
            <span className="block max-w-[12rem] truncate" title={text}>
              {text}
            </span>
          );
        },
      },
      frist('Frist'),
      score,
      status,
    ];
  }

  if (art === 'wettbewerb') {
    return [
      titel,
      {
        key: 'reward',
        header: 'Gewinn',
        render: (p) =>
          [p.reward_kind ? REWARD_KIND_TEXT[p.reward_kind] : null, p.amount_max ? formatEuro(p.amount_max) : null]
            .filter(Boolean)
            .join(' · ') || leer,
      },
      { key: 'effort', header: 'Aufwand', render: (p) => (p.estimated_effort ? EFFORT_TEXT[p.estimated_effort] : leer) },
      frist('Frist'),
      score,
      status,
    ];
  }

  return [
    titel,
    { key: 'event_date', header: 'Datum', mobil: 'unter', render: (p) => <EventDatum programm={p} /> },
    { key: 'location', header: 'Ort', render: (p) => p.location ?? leer },
    { ...frist('Anmeldeschluss'), mobil: undefined },
    score,
    status,
  ];
}

/**
 * Was ein Mensch an diesem Eintrag tun muss, als Zeile unter dem Titel:
 * Prüfung fällig, Quelle gesperrt, umgezogen … Ohne Bedarf keine Zeile.
 */
function Handlungsbedarf({ programm }: { programm: RadarProgram }) {
  const quelle = quelleBrauchtJemanden(programm.source_state);
  if (!quelle && !programm.manual_check_due) return null;
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-normal">
      <PruefHinweis programm={programm} />
      {quelle && <Quellzustand zustand={programm.source_state} />}
    </div>
  );
}
